import React, { useMemo } from "react";
import UpNextShowTile from "../UpNextShowTile";
import LoadingSpinner from "../common/LoadingSpinner";
import { useScheduleState } from "../../hooks/useScheduleState";
import { TimeZoneUtils } from "../../utils/timeZoneUtils";

/**
 * UpNextSectionContent - Content-only version without container styling
 * Used by HomePage variations to avoid styling conflicts
 */
const UpNextSectionContent: React.FC = () => {
  const { upcomingToday, upcomingTomorrow, loading, error } = useScheduleState();

  const {
    displayShows,
    moreCount,
  } = useMemo(() => {
    const now = TimeZoneUtils.getCurrentLondonTime();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowStart = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
    const dayAfterTomorrow = new Date(tomorrowStart.getTime() + 24 * 60 * 60 * 1000);

    const shouldExtend = upcomingToday.length <= 3 && upcomingTomorrow.length > 0;
    const combined = shouldExtend ? [...upcomingToday, ...upcomingTomorrow] : upcomingToday;

    const annotated = combined.map((show) => {
      const start = TimeZoneUtils.parseShowDateTime(show.date_start_time);
      const isTomorrow = start >= tomorrowStart && start < dayAfterTomorrow;
      const datePrefix = isTomorrow
        ? start.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })
        : undefined;

      return { show, datePrefix };
    });

    const slice = annotated.slice(0, 6);
    const remaining = combined.length > 6 ? combined.length - 6 : 0;

    return {
      displayShows: slice,
      moreCount: remaining,
    };
  }, [upcomingToday, upcomingTomorrow]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" text="Loading upcoming shows..." />
      </div>
    );
  }

  if (!displayShows.length) {
    return null;
  }

  return (
    <>
      {/* Section header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Up Next</h2>
        </div>
        {error && (
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded px-2 py-1">
            <span className="text-yellow-400 text-xs">Limited data</span>
          </div>
        )}
      </div>

      {/* Shows grid - horizontal scroll on mobile, grid on desktop */}
      <div className="relative">
        {/* Mobile: Horizontal scroll */}
        <div className="flex gap-3 overflow-x-auto pb-2 sm:hidden scrollbar-hide">
          {displayShows.map(({ show, datePrefix }, index) => (
            <div key={`${show.show}-${index}`} className="flex-shrink-0 w-32">
              <UpNextShowTile show={show} isPriority={index < 2} datePrefix={datePrefix} />
            </div>
          ))}
          {moreCount > 0 && (
            <div className="flex-shrink-0 w-32 flex items-center justify-center">
    <div className="text-center text-slate-200">
              <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center mb-2">
                <span className="text-sm font-medium">
                    +{moreCount}
                </span>
              </div>
                <p className="text-xs">more shows</p>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: Grid */}
        <div className="hidden sm:grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {displayShows.map(({ show, datePrefix }, index) => (
            <UpNextShowTile key={`${show.show}-${index}`} show={show} isPriority={index < 2} datePrefix={datePrefix} />
          ))}
        </div>

      </div>
    </>
  );
};

export default UpNextSectionContent;
